import { produce } from 'immer';
import { find, get, includes, isFunction, isNil, split } from 'lodash';
import { createLogic } from 'redux-logic';
import { v4 as uuid } from 'uuid';

import { featureFlags, FEATURE_FLAGS_STORAGE_KEY } from 'common/featureFlags/featureFlags';
import { resetLastAuthUser } from 'common/graphql/client';
import { setLanguage } from 'common/i18n/i18n';
import { AuditCreationEventsEnum, AuditEntityIdEnum } from 'common/log/auditevents';
import { CIAuditLogger } from 'common/log/ci.auditlogger';
import oktaAuth from 'module/auth/okta/oktaAuth';
import { updateEconsentSignature } from 'module/econsent/utils/endpoints';

import { CIAnalytics } from '../analytics/analytics.provider';
import { AuthUserType, RoleTypeEnum, UserType } from '../graphql/types';
import { CILogger } from '../log/log.provider';

import { AuthActions, AuthActionType } from './actions';
import { AuthLocalStorageKeys } from './constants';
import {
    addGraphQLDataToUser,
    buildAuthUserFromCognitoSession,
    changePassword,
    checkValidSwitchUser,
    deleteLastPagesOpened,
    determineSelectedActiveStudySite,
    getGraphQLClient,
    login,
    logPatientLogin,
    logUserLogin,
    mapGraphQLToPatient,
    markPasswordUsed,
    persistAuthUsers,
    resetUserData,
    revokeCognitoTokenAndSignOut,
    updateActiveStudySite,
    updateAuthUsers,
    updateSwitchUserState,
    updateUsersLastLoggedInTime,
    userChangePassword,
} from './helpers';
import { updateUserToSPP } from './queries';
import type { ActiveStudySite, AuthState, AuthUser, PatientAccess } from './types';

function getDefaultState(): AuthState {
    const defaultState: AuthState = {
        activeMeeting: false,
        thumbnailMode: false,
        activeUser: null,
        submitted: false,
        complete: false,
        error: null,
        activeStudySite: {
            activeStudy: null,
            activeSite: null,
            activeStudySiteReady: false,
        },
        switchingUser: false,
        validSwitchUser: false,
        tempUser: null,
        patientAccess: {
            status: 'IDLE',
            currentPatientID: null,
            activationStatus: false,
            patientPK: null,
            activationCode: null,
            activityCompletionStatus: false,
            caregivers: [],
            isCaregiverUser: false,
        },
        meetingDetails: {
            id: '',
            name: '',
            isAdmin: false,
        },
        passwordChangeStatus: 'IDLE',
        newPasswordRequested: false,
        ssoLoading: false,
        userRoleSwitchPending: false,
    };

    const activeUser = localStorage.getItem('activeUser');
    const activeStudySite = localStorage.getItem(AuthLocalStorageKeys.activeStudySite);
    const switchingUser = localStorage.getItem('switchingUser');
    const patientAccess = localStorage.getItem('patientAccess');

    if (activeUser) {
        const activeUserVal = JSON.parse(activeUser);
        defaultState.activeUser = activeUserVal;
    }
    if (activeStudySite) {
        defaultState.activeStudySite = JSON.parse(activeStudySite);
    }
    if (switchingUser) {
        defaultState.switchingUser = JSON.parse(switchingUser);
    }
    if (patientAccess) {
        defaultState.patientAccess = JSON.parse(patientAccess);
    }

    return defaultState;
}

export const authReducers = (state = getDefaultState(), action): AuthState => {
    switch (action.type) {
        case AuthActionType.HANDLE_PASSWORD_CHANGE_STATUS:
            return produce(state, (newState) => {
                newState.passwordChangeStatus = action.payload;

                return newState;
            });
        case AuthActionType.HANDLE_SWITCH_USER:
            return produce(state, (newState) => {
                newState.switchingUser = action.payload;
                newState.patientAccess = {
                    ...action.meta,
                    // Since in the Login page we see an Activate Patient button when patient hasn't been activated
                    // and this is the default behavior for LAR caregivers, ensure to mark patient as
                    // activated to avoid showing button
                    activationStatus: action.meta.hasLAR ? true : action.meta.activationStatus,
                };
                updateSwitchUserState(newState); //Store Patient Access State in localStorage

                return newState;
            });
        case AuthActionType.HANDLE_PATIENT_ACCESS:
            return produce(state, (newState) => {
                const passedPatientAccess = action.payload;
                const patientAccess: PatientAccess = {
                    ...state.patientAccess,
                    ...passedPatientAccess,
                };
                newState.patientAccess = patientAccess;
                updateSwitchUserState(newState); //Update Patient Access State in localStorage

                return newState;
            });
        case AuthActionType.RESET_ACTIVE_STUDY_SITE:
            return produce(state, (newState) => {
                newState.activeStudySite = {
                    activeStudy: null,
                    activeSite: null,
                    activeStudySiteReady: false,
                };
                localStorage.setItem(AuthLocalStorageKeys.activeStudySite, JSON.stringify(newState.activeStudySite));

                return newState;
            });
        case AuthActionType.HANDLE_SWITCH_USER_TEMP_USER:
            return produce(state, (newState) => {
                newState.tempUser = null;

                return newState;
            });
        case AuthActionType.UPDATE_ACTIVE_STUDY_SITE:
            return produce(state, (newState) => {
                const { study, site, updatedUser } = action.payload;
                const selectedActiveStudySite: ActiveStudySite = {
                    activeStudy: study,
                    activeSite: site,
                    activeStudySiteReady: true,
                };
                newState.activeStudySite = selectedActiveStudySite;
                if (updatedUser) {
                    newState.activeUser = updatedUser;
                    persistAuthUsers(newState);
                } else {
                    updateActiveStudySite(newState);
                }
                CIAnalytics.getInstance().identifyUser(state.activeUser);

                return newState;
            });
        case AuthActionType.LOGIN_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.SWITCH_LOGIN_REQUEST:
            return produce(state, (newState): void => {
                if (state.patientAccess.status !== 'IDLE') {
                    // Reset the patient status to INPROGRESS State
                    // This can only happen if patient already tried once and get failed
                    // * Would not be set for normal switch user flow
                    newState.patientAccess.status = 'INPROGRESS';
                }
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.LOGIN_SUCCESS:
            return produce(state, (newState): void => {
                const authUser: AuthUser = action.payload;
                localStorage.removeItem('sessionLastActivity');
                authUser.sessionId = uuid();
                newState.activeUser = authUser;
                newState.error = null;

                // Set active study/site only when not switching users
                if (state.switchingUser !== true) {
                    newState.activeStudySite = determineSelectedActiveStudySite(authUser);
                }

                newState.submitted = false;
                newState.complete = true;

                // Setting state here to indicate for login page
                newState.validSwitchUser = !isNil(authUser.isValidSwitchUser) && authUser.isValidSwitchUser;

                if (isNil(state?.switchingUser) || state.switchingUser === false) {
                    newState.switchingUser = false;
                }

                // persist activeStudySite
                if (featureFlags.fxIntegration && newState.validSwitchUser) {
                    newState.activeStudySite = JSON.parse(localStorage.getItem(AuthLocalStorageKeys.activeStudySite));
                }

                persistAuthUsers(newState);
                updateSwitchUserState(newState);

                if (authUser.userType === AuthUserType.PATIENT) {
                    logPatientLogin(newState);
                } else {
                    logUserLogin(newState, state.switchingUser);
                }
            });
        case AuthActionType.LOGIN_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.LOGOUT_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.RESET_USER_DATA:
            return produce(state, (newState): void => {
                newState.submitted = true;
            });
        case AuthActionType.RESET_COMPLETE:
            return produce(state, (newState): void => {
                newState.complete = false;
            });
        case AuthActionType.LOGOUT_SUCCESS: {
            const { switchUser } = action.payload;

            return produce(state, (newState): void => {
                const isPatient = get(state, 'activeUser.userType', '') === AuthUserType.PATIENT;
                if (isPatient) {
                    // Study Audit logging
                    let patientStudyID;
                    let patientSiteID;

                    const patientStudy = state?.activeUser?.userStudies?.[0];
                    if (patientStudy) {
                        patientStudyID = patientStudy.studyId;
                        if (!isNil(patientStudyID) && includes(patientStudyID, '#')) {
                            patientStudyID = split(patientStudyID, '#')[0];
                        }

                        const patientSite = patientStudy.userStudySites?.[0];
                        if (patientSite) {
                            patientSiteID = patientSite.siteId;
                        }
                    }
                    CIAuditLogger.getInstance().log(
                        AuditCreationEventsEnum.Logout,
                        `Patient user ${newState.activeUser.username} logged out of Patient Web`,
                        null,
                        {
                            relatedEntityName: AuditEntityIdEnum.User,
                            relatedEntityId: newState.activeUser.username,
                            studyId: patientStudyID,
                            siteId: patientSiteID,
                            subjectId: newState.activeUser.pk,
                        }
                    );

                    //Kibana logging
                    CILogger.getInstance().info(
                        {
                            message: `Patient ${state.activeUser.pk} logout of Patient Web.`,
                        },
                        {
                            studyId: patientStudyID,
                            siteId: patientSiteID,
                            subjectId: state.activeUser.pk,
                        }
                    );
                } else {
                    //Kibana logging
                    CILogger.getInstance().info(
                        `${state.activeUser?.username} logged out ${state.switchingUser ? 'by switch user.' : ''}`
                    );
                }

                // If switch user flag is ON then don't remove the active user
                newState.activeUser = switchUser ? state.activeUser : null;
                newState.submitted = false;
                // make the complete false to enable second try on switch user modal
                newState.complete = !state.switchingUser;
                // Switch User - If already opened don't close
                newState.switchingUser = !!state.switchingUser;

                // Clear active Study Site only if its a normal logout
                if (!state.switchingUser) {
                    // activeStudySiteReady to true here because to prevent redirection from dashboard to ActiveStudySite Screen
                    // during logout
                    newState.activeStudySite = {
                        activeStudy: null,
                        activeSite: null,
                        activeStudySiteReady: true,
                    };
                }

                const activeUserFromSession = localStorage.getItem('activeUser');
                const activeUser: AuthUser = activeUserFromSession && JSON.parse(activeUserFromSession);

                if (!switchUser) {
                    newState.patientAccess.status = 'IDLE';
                    newState.patientAccess.currentPatientID = null;
                    newState.patientAccess.caregivers = [];
                }

                if (featureFlags.fxIntegration && isPatient) {
                    newState.switchingUser = false;
                    updateSwitchUserState(newState);
                }

                if (window.pendo && isFunction(window.pendo.clearSession)) {
                    window.pendo.clearSession();
                }

                newState.ssoLoading = true;

                const isOkta = activeUser?.authenticationMethod === 'OKTA';

                updateAuthUsers(newState, state.switchingUser, isOkta);

                if (!state.switchingUser) {
                    revokeCognitoTokenAndSignOut(activeUser);
                }
            });
        }
        case AuthActionType.POLICY_REQUEST:
            return produce(state, (newState): void => {
                newState.submitted = true;
                newState.complete = false;
                newState.error = null;
            });
        case AuthActionType.POLICY_SUCCESS:
            return produce(state, (newState): void => {
                const { activeUser } = action.payload;
                newState.activeUser = activeUser;
                newState.submitted = false;
                newState.complete = true;
                persistAuthUsers(newState);
            });
        case AuthActionType.POLICY_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CHANGE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }
            });
        case AuthActionType.CHANGE_PASS_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CREATE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                const { authUser } = action.payload as { authUser: AuthUser; newPassword: string };
                authUser.sessionId = uuid();

                if (state.switchingUser) {
                    //Storing user in tempUser state because during switch user there is already a user in activeUser state.
                    newState.tempUser = authUser;
                } else {
                    newState.activeUser = authUser;
                }

                newState.submitted = false;
                newState.complete = false;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }
            });
        case AuthActionType.CREATE_PASS_FAILURE:
            return produce(state, (newState): void => {
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.USER_CHANGE_PASS_SUCCESS:
            return produce(state, (newState): void => {
                const { authUser, oldPassword, newPassword } = action.payload as {
                    authUser: AuthUser;
                    oldPassword: string;
                    newPassword: string;
                };
                authUser.sessionId = uuid();
                newState.activeUser = authUser;

                if (state.passwordChangeStatus !== 'IDLE') {
                    newState.passwordChangeStatus = 'SUCCESS';
                }
                newState.submitted = false;
                newState.complete = false;
                // removing the error from auth to avoid showing it on login page after successful pwd change
                if (state.error?.message === 'cognito.PasswordUnavailable') {
                    newState.error = null;
                } else {
                    newState.error = { ...state.error, trigger: null };
                }

                // Update Econsent Signature
                updateEconsentSignature(authUser.pk, oldPassword, newPassword);

                persistAuthUsers(newState);
            });
        case AuthActionType.USER_CHANGE_PASS_FAILURE:
            return produce(state, (newState): void => {
                if (state.passwordChangeStatus !== 'IDLE') {
                    newState.passwordChangeStatus = 'FAILED';
                }
                newState.submitted = false;
                newState.complete = false;
                newState.error = action.payload;
            });
        case AuthActionType.CLEAR_ERROR:
            return produce(state, (newState): void => {
                newState.error = null;
            });
        case AuthActionType.SWITCH_USER_FAILED_STATUS: {
            const userType = action.payload;

            return produce(state, (newState): void => {
                if (state.patientAccess.status === 'INPROGRESS') {
                    const { error } = action.meta;
                    if (error) {
                        newState.patientAccess.status = error;
                    } else {
                        newState.patientAccess.status = userType === 'Patient' ? 'PATIENT_FAILED' : 'USER_FAILED';
                    }
                }
                newState.submitted = false;
                newState.complete = true;
                newState.validSwitchUser = false;
            });
        }
        case AuthActionType.USER_UPDATE_SECURITY_QUESTIONS: {
            const { securityQuestion1Text, securityQuestion2Text, securityQuestion3Text } = action.payload;

            return produce(state, (newState): void => {
                newState.activeUser.securityQuestion1Text = securityQuestion1Text;
                newState.activeUser.securityQuestion2Text = securityQuestion2Text;
                newState.activeUser.securityQuestion3Text = securityQuestion3Text;
                persistAuthUsers(newState);
            });
        }
        case AuthActionType.TELEVISIT_REQUEST: {
            const { data, active } = action.payload;

            return produce(state, (newState): void => {
                newState.activeMeeting = active;
                newState.meetingDetails.id = data.id || '';
                newState.meetingDetails.name = data.name || '';
                newState.meetingDetails.isAdmin = data.isAdmin || false;
                if (!active) {
                    newState.thumbnailMode = false;
                }
            });
        }
        case AuthActionType.TELEVISIT_THUMBNAIL_MODE:
            return produce(state, (newState): void => {
                newState.thumbnailMode = !state.thumbnailMode;
            });
        case AuthActionType.UPDATE_PATIENT_STUDY_ID:
            return produce(state, (newState): void => {
                const studyId = action.payload.data;
                newState.activeUser.studyId = studyId;
                newState.activeUser.userStudies[0].studyId = studyId;
                if (newState.activeStudySite.activeStudy.studyId) {
                    newState.activeStudySite.activeStudy.studyId = studyId;
                }
                if (newState.activeStudySite.activeSite.studyId) {
                    newState.activeStudySite.activeSite.studyId = studyId;
                }
                persistAuthUsers(newState);
            });
        case AuthActionType.UPDATE_PATIENT_ARM_ID:
            return produce(state, (newState): void => {
                const armId = action.payload.data;
                newState.activeUser.armId = armId;
                persistAuthUsers(newState);
            });
        case AuthActionType.ACTIVATION_PASSWORD_REQUEST:
            return produce(state, (newState): void => {
                newState.newPasswordRequested = action.payload.data;
            });
        case AuthActionType.SET_SSO_LOADING:
            return produce(state, (newState) => {
                newState.ssoLoading = action.payload.data;
            });
        case AuthActionType.USER_ROLE_SWITCH_PENDING:
            return produce(state, (newState) => {
                newState.userRoleSwitchPending = action.payload.isPending;
            });
        default:
            return state;
    }
};

const LoginLogic = createLogic({
    type: AuthActionType.LOGIN_REQUEST,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const { username, password, ssoTokens } = action.payload;
        login(username, password, ssoTokens, dispatch)
            .then((authUser) => deleteLastPagesOpened(authUser))
            .then((authUser) => updateUsersLastLoggedInTime(authUser))
            .then((authUser): void => {
                setLanguage(authUser?.preferredLanguage || 'en-US');
                // to avoid showing guides in policy page, check if the current policy and user accepted policy are same
                if (
                    (authUser.currentPolicy?.[0] &&
                        authUser.acceptedVersionPP === authUser.currentPolicy[0].majorVersion) ||
                    !includes(authUser.permissions, 'SubscriberPP_Read')
                ) {
                    CIAnalytics.getInstance().identifyUser(authUser);
                }
                dispatch(AuthActions.loginSucceeded(authUser));
            })
            .catch((err): void => {
                dispatch(AuthActions.loginFailed(err));
            })
            .finally(done);
    },
});

const ResetUserDataLogic = createLogic<{ auth: AuthState }>({
    type: AuthActionType.RESET_USER_DATA,
    process({ getState }, dispatch, done): void {
        const authUser = getState()?.auth?.activeUser;

        resetUserData(authUser)
            .then(() => {
                // TODO: there should be a better / cleaner approach to reset data
                window.location.href = '/';
            })
            .catch(() => {
                dispatch(AuthActions.logoutRequest());
            })
            .finally(() => {
                done();
            });
    },
});

const finishSwitchUserLogic = createLogic<{ auth: AuthState }, { authUser: AuthUser }>({
    type: AuthActionType.FINISH_SWITCH_USER,
    process({ getState, action }, dispatch, done): void {
        const { authUser } = action.payload;
        const { auth } = getState();

        const validSwitchUser = checkValidSwitchUser(auth.activeStudySite, authUser);
        let siteActivityAccess = false;

        // * Not Equal to IDLE will prevent patient from entering from normal switch user popups
        // * If they enter they will get `Not assigned to study sites` error for now

        if (auth.patientAccess.status !== 'IDLE') {
            const userRoleType = authUser?.roleVClaim?.name;
            if (authUser.userType === UserType.Patient) {
                // If username is associated with a caregiver list, then match it to allow proceeding with login
                // This will only happen when switching users
                const caregivers = auth.patientAccess?.caregivers || [];
                const hasCaregivers = caregivers.length > 0;
                const isUserInCaregiversList = find(caregivers, ['name', authUser.username]);
                const isCaregiverUser = auth.patientAccess?.isCaregiverUser;

                const caregiverUserNotInList = isCaregiverUser && hasCaregivers && !isUserInCaregiversList;

                const nonCaregiverUserInList = !isCaregiverUser && hasCaregivers && isUserInCaregiversList;

                if (caregiverUserNotInList || nonCaregiverUserInList) {
                    resetLastAuthUser(auth.activeUser.pk, authUser.pk, authUser.authenticationMethod);
                    dispatch(
                        AuthActions.loginFailed({
                            translationKey: 'cognito.NotAuthorizedException',
                            sourceError: {
                                code: 'NotAuthorizedException',
                                name: 'NotAuthorizedException',
                            },
                            code: 'NotAuthorizedException',
                        })
                    );
                    done();

                    return;
                }

                siteActivityAccess = authUser.username === auth.patientAccess.currentPatientID;
                // Check if this valid patient already completed the activity
                if (siteActivityAccess && auth.patientAccess.activityCompletionStatus) {
                    resetLastAuthUser(auth.activeUser.pk, authUser.pk, authUser.authenticationMethod);
                    dispatch(
                        AuthActions.switchUserFailedStatus(authUser.userType, {
                            error: 'PATIENT_COMPLETED',
                        })
                    );
                    done();

                    return;
                }
            } else if (
                userRoleType !== RoleTypeEnum.SiteInvestigator &&
                userRoleType !== RoleTypeEnum.SiteCoordinator
            ) {
                resetLastAuthUser(auth.activeUser.pk, authUser.pk, authUser.authenticationMethod);
                // Disable access to the activity if the user is not Site related Roles
                dispatch(AuthActions.switchUserFailedStatus(authUser.userType, { error: 'PATIENT_FAILED' }));
                done();

                return;
            }
        }

        if (validSwitchUser || siteActivityAccess) {
            setLanguage(authUser?.preferredLanguage || 'en-US');

            CIAnalytics.getInstance().identifyUser(authUser);

            dispatch(AuthActions.logoutSucceeded({ switchUser: true }));

            // Setting valid Switch user prop to AuthUser to pass information to switch user modal
            authUser.isValidSwitchUser = true;
            // If same User entered again via switch user no need to replace LastAuthUser of Cognito
            if (authUser.pk !== auth.activeUser.pk) {
                resetLastAuthUser(authUser.pk, auth.activeUser.pk, authUser.authenticationMethod);
            }
            dispatch(AuthActions.loginSucceeded(authUser));

            done();
        } else {
            resetLastAuthUser(auth.activeUser.pk, authUser.pk, authUser.authenticationMethod);
            dispatch(AuthActions.switchUserFailedStatus(authUser.userType));

            done();
        }
    },
});

const SwitchUserLoginLogic = createLogic({
    type: AuthActionType.SWITCH_LOGIN_REQUEST,
    process({ getState, action }, dispatch, done): void {
        // @ts-ignore
        const { username, password, ssoTokens } = action.payload;

        login(username, password, ssoTokens)
            .then((authUser) => deleteLastPagesOpened(authUser)) // If the Second user authentication is success then delete the lastPagesOpened
            .then((authUser) => updateUsersLastLoggedInTime(authUser))
            .then((authUser): void => {
                const { auth } = getState() as { auth: AuthState };

                if (featureFlags.fxIntegration && auth.activeUser.authenticationMethod === 'OKTA') {
                    localStorage.setItem('switchUserAuthUser', JSON.stringify(authUser));
                    oktaAuth?.signOut();
                } else {
                    dispatch(AuthActions.finishSwitchUser(authUser));
                }
            })
            .catch((err): void => {
                dispatch(AuthActions.loginFailed(err));
            })
            .finally(done);
    },
});

const LogoutLogic = createLogic({
    type: AuthActionType.LOGOUT_REQUEST,
    process(_, dispatch, done): void {
        dispatch(AuthActions.logoutSucceeded({ switchUser: false }));
        CIAnalytics.getInstance().clearSession();
        setTimeout(() => {
            localStorage.removeItem('postLoginRedirectUrl');

            const preservedFlags = sessionStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
            sessionStorage.clear();

            if (preservedFlags) {
                sessionStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, preservedFlags);
            }

            done();
        }, 50);
    },
});

/**
 * Responsible for creating password for new users
 */
const ChangePassLogic = createLogic({
    type: AuthActionType.CHANGE_PASS,
    process({ getState, action }, dispatch, done): void {
        // @ts-ignore
        const { newPassword } = action.payload;
        changePassword(newPassword)
            .then((authUser): void => {
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                CIAnalytics.getInstance().identifyUser(authUser);

                // Should not login the new user in Patient Activation/Registration Flow
                // Store the successful user in the tempUser to handle it accordingly
                //@ts-ignore
                const { auth }: { auth: AuthState } = getState();
                if (auth.patientAccess.status !== 'IDLE') {
                    dispatch(AuthActions.createPasswordSucceeded(authUser, newPassword));
                } else {
                    dispatch(AuthActions.loginSucceeded(authUser));
                }
            })
            .catch((err): void => {
                dispatch(AuthActions.changePasswordFailed(err));
            })
            .finally(done);
    },
});

const UserChangePassLogic = createLogic({
    type: AuthActionType.USER_CHANGE_PASS,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const { oldPassword, newPassword } = action.payload;
        userChangePassword(oldPassword, newPassword)
            .then((authUser): void => {
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                dispatch(AuthActions.userChangePasswordSucceeded(authUser, oldPassword, newPassword));
            })
            .catch((err): void => {
                dispatch(AuthActions.userChangePasswordFailed(err));
            })
            .finally(done);
    },
});

const CreatePassLogic = createLogic({
    type: AuthActionType.CREATE_PASSWORD,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const { newPassword } = action.payload;
        changePassword(newPassword)
            .then((authUser): void => {
                CIAnalytics.getInstance().identifyUser(authUser);
                if (authUser?.type !== AuthUserType.PATIENT) {
                    markPasswordUsed(authUser?.pk, newPassword);
                }
                dispatch(AuthActions.createPasswordSucceeded(authUser, newPassword));
            })
            .catch((err): void => {
                dispatch(AuthActions.createPasswordFailed(err));
            })
            .finally(done);
    },
});

const PolicyLogic = createLogic({
    type: AuthActionType.POLICY_REQUEST,
    process({ action }, dispatch, done): void {
        // @ts-ignore
        const data = action.payload;
        const { activeUser } = data;
        getGraphQLClient()
            .mutate({
                mutation: updateUserToSPP,
                variables: {
                    userId: activeUser?.pk,
                    pVersion: activeUser.currentPolicy?.[0]?.majorVersion,
                },
            })
            .then((): void => {
                dispatch(AuthActions.policySucceeded(data));
            })
            .catch((err): void => {
                dispatch(AuthActions.policyFailed(err));
            })
            .then(() => done());
    },
});

export const ReduxLogic = [
    LoginLogic,
    ChangePassLogic,
    PolicyLogic,
    UserChangePassLogic,
    CreatePassLogic,
    LogoutLogic,
    ResetUserDataLogic,
    SwitchUserLoginLogic,
    finishSwitchUserLogic,
];

/**
 * For unit test use only
 */
export const UnderTest = {
    buildAuthUserFromCognitoSession,
    mapGraphQLToUser: addGraphQLDataToUser,
    AuthActions,
    authReducers,
    AuthActionType,
    checkValidSwitchUser,
    mapGraphQLToPatient,
};
